import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { TableList, Typography, TextButton, WithInfoIcon, useDevice, ScopedThemeProvider, useOverlayContext } from 'seagull';
import { getURLWithFullPageInvoice } from '~/components/FullPageInvoice/FullPageInvoice.utils';
import { InvoiceAccountingIntegrationStatus } from '~/components/InvoiceAccountingIntegrationStatus';
import { getMobileTheme } from '~/constants';
import { timeModel } from '~/models';
import { PayoutAccountingIntegrationStatus } from '~/pages/PayoutsDashboard/PayoutAccountingIntegrationStatus/PayoutAccountingIntegrationStatus';
import PayoutAccountingSection from '~/pages/PayoutsDashboard/PayoutsQboSection/PayoutAccountingSection';
import { selectors } from '~/store/state-selectors';
import { useStore } from '~/store/store-context';
import { Accounting } from '~/types/api.model';
import PayoutFailedCallout from '../PayoutFailureMessage/PayoutFailedCallout';
import styles from './PayoutsDashboardRow.module.scss';
import { getStatusSkin, getStatusText } from './PayoutsDashboardRow.utils';
function ExpandedPayoutContent(props) {
    const { payout, testId, invoiceHeaders, showQboIntegration, showXeroIntegration, isMobileDevice } = props;
    const { hideBottomSheet } = useOverlayContext();
    const columnDefs = [
        {
            width: showQboIntegration || showXeroIntegration ? 160 : 120,
            mobile: {}
        },
        {
            width: 80,
            mobile: {
                label: {
                    text: 'Type',
                    hideColon: true
                },
                spaceBetween: true
            }
        },
        {
            width: [
                140,
                180
            ],
            mobile: {
                label: {
                    text: 'Company name',
                    hideColon: true
                },
                spaceBetween: true
            }
        },
        {
            width: [
                90,
                100
            ],
            mobile: {
                label: {
                    text: 'Issue date',
                    hideColon: true
                },
                spaceBetween: true
            }
        },
        {
            width: [
                90,
                100
            ],
            mobile: {
                label: {
                    text: 'Due date',
                    hideColon: true
                },
                spaceBetween: true
            }
        },
        {
            width: 100,
            hAlign: 'end',
            mobile: {
                label: {
                    text: 'Gross amount',
                    hideColon: true
                },
                spaceBetween: true
            }
        },
        {
            width: 80,
            hAlign: 'end',
            mobile: {
                label: {
                    text: 'Fees',
                    hideColon: true
                },
                spaceBetween: true
            }
        },
        {
            width: 110,
            hAlign: 'end',
            mobile: {
                label: {
                    text: 'Net amount',
                    hideColon: true,
                    weight: 'normal'
                },
                value: {
                    weight: 'normal'
                },
                spaceBetween: true,
                divider: true
            }
        }
    ];
    const headerItems = invoiceHeaders.map((text)=>({
            value: text
        }));
    function renderFeesCell(invoice) {
        return /*#__PURE__*/ _jsx(WithInfoIcon, {
            testId: "fees-".concat(invoice.id),
            infoTestId: "fees-tooltip-".concat(invoice.id),
            showInfo: !invoice.totalFees.isZero(),
            className: styles.fees,
            info: /*#__PURE__*/ _jsxs("div", {
                className: styles.feesTooltip,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        skin: "invertedStandard",
                        weight: "bold",
                        children: "Transaction fees"
                    }),
                    !invoice.fee.isZero() && /*#__PURE__*/ _jsxs("div", {
                        "data-testid": "fees-tooltip-anchor-fee-".concat(invoice.id),
                        children: [
                            invoice.fee.display(),
                            " Anchor"
                        ]
                    }),
                    !invoice.processingFee.isZero() && /*#__PURE__*/ _jsxs("div", {
                        "data-testid": "fees-tooltip-processing-fee-".concat(invoice.id),
                        children: [
                            invoice.processingFee.display(),
                            " Credit card"
                        ]
                    }),
                    !invoice.serviceFeeRefund.isZero() && /*#__PURE__*/ _jsxs("div", {
                        "data-testid": "fees-tooltip-service-fee-refund-".concat(invoice.id),
                        children: [
                            invoice.serviceFeeRefund.neg().display(),
                            " Anchor fee refund"
                        ]
                    }),
                    !invoice.customerProcessingFee.isZero() && /*#__PURE__*/ _jsxs("div", {
                        "data-testid": "fees-tooltip-customer-processing-fee-".concat(invoice.id),
                        children: [
                            invoice.customerProcessingFee.display(),
                            " Credit card fees returned to the client"
                        ]
                    }),
                    !invoice.disputeFee.isZero() && /*#__PURE__*/ _jsxs("div", {
                        "data-testid": "fees-tooltip-dispute-fee-".concat(invoice.id),
                        children: [
                            invoice.disputeFee.display(),
                            " chargebacks fee for a dispute received on invoice",
                            ' ',
                            invoice.formattedNumber
                        ]
                    })
                ]
            }),
            children: /*#__PURE__*/ _jsx(Typography, {
                ellipsis: true,
                skin: "secondary",
                children: invoice.totalFees.display()
            })
        });
    }
    const callouts = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PayoutFailedCallout, {
                payout: payout,
                testId: "".concat(testId, "-failure-reason")
            }),
            payout.isPaid() && showQboIntegration && !!payout.qboIntegration() && /*#__PURE__*/ _jsx(PayoutAccountingSection, {
                testId: "".concat(testId, "-qbo-section"),
                payoutId: payout.id(),
                payoutIntegration: payout.qboIntegration(),
                accounting: Accounting.QBO
            }),
            payout.isPaid() && showXeroIntegration && !!payout.xeroIntegration() && /*#__PURE__*/ _jsx(PayoutAccountingSection, {
                testId: "".concat(testId, "-xero-section"),
                payoutId: payout.id(),
                payoutIntegration: payout.xeroIntegration(),
                accounting: Accounting.Xero
            })
        ]
    });
    return /*#__PURE__*/ _jsxs(ScopedThemeProvider, {
        theme: isMobileDevice ? getMobileTheme() : {},
        maxHeight: false,
        className: cn(styles.expandedWrapper, {
            [styles.mobile]: isMobileDevice
        }),
        children: [
            !isMobileDevice && callouts,
            /*#__PURE__*/ _jsxs(TableList.Provider, {
                columnDefs: columnDefs,
                variant: "minimal",
                justifyBetween: true,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: cn(styles.expandedHeader, {
                            [styles.mobile]: isMobileDevice
                        }),
                        children: /*#__PURE__*/ _jsx(TableList.Header, {
                            items: headerItems,
                            color: "transparent"
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        "data-testid": "invoice-rows",
                        children: payout.invoices().map((invoice, idx)=>{
                            var _payout_qboIntegrationForInvoice, _payout_xeroIntegrationForInvoice;
                            return /*#__PURE__*/ _jsx(TableList.Row, {
                                testId: "invoice-row-".concat(invoice.id),
                                mobile: {
                                    skin: 'mobileGray'
                                },
                                items: compact([
                                    {
                                        value: /*#__PURE__*/ _jsxs("div", {
                                            className: styles.invoiceNumber,
                                            children: [
                                                /*#__PURE__*/ _jsx(TextButton, {
                                                    as: Link,
                                                    testId: "".concat(testId, "-invoice-link-").concat(idx),
                                                    "data-heapid": "payment-invoice-link",
                                                    to: getURLWithFullPageInvoice({
                                                        invoiceId: invoice.id,
                                                        relationshipId: invoice.relationshipId
                                                    }),
                                                    onClick: hideBottomSheet,
                                                    children: invoice.formattedNumber
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: styles.invoiceStatus,
                                                    children: [
                                                        showQboIntegration && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                                                            testId: "".concat(testId, "-invoice-qbo-").concat(idx),
                                                            invoiceIntegration: (_payout_qboIntegrationForInvoice = payout.qboIntegrationForInvoice(invoice.id)) === null || _payout_qboIntegrationForInvoice === void 0 ? void 0 : _payout_qboIntegrationForInvoice.integration,
                                                            accountingType: Accounting.QBO
                                                        }),
                                                        showXeroIntegration && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                                                            testId: "".concat(testId, "-invoice-xero-").concat(idx),
                                                            invoiceIntegration: (_payout_xeroIntegrationForInvoice = payout.xeroIntegrationForInvoice(invoice.id)) === null || _payout_xeroIntegrationForInvoice === void 0 ? void 0 : _payout_xeroIntegrationForInvoice.integration,
                                                            accountingType: Accounting.Xero
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    },
                                    {
                                        value: invoice.type
                                    },
                                    {
                                        value: /*#__PURE__*/ _jsx(Typography, {
                                            ellipsis: true,
                                            skin: "secondary",
                                            children: invoice.customer
                                        })
                                    },
                                    {
                                        value: timeModel.formatDate(invoice.createdOn)
                                    },
                                    {
                                        value: timeModel.formatDate(invoice.dueDate)
                                    },
                                    {
                                        value: invoice.totalAmount.display()
                                    },
                                    {
                                        value: renderFeesCell(invoice)
                                    },
                                    {
                                        testId: "deposit-amount-".concat(invoice.id),
                                        value: invoice.depositAmount.display()
                                    }
                                ])
                            }, invoice.id + invoice.type);
                        })
                    })
                ]
            }),
            isMobileDevice && callouts
        ]
    });
}
export default function PayoutsDashboardRow(props) {
    const { index, invoiceHeaders } = props;
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const testId = "payments-row-".concat(index);
    const { isMobileDevice } = useDevice();
    const [payout, setPayout] = useState(props.payout);
    useEffect(()=>setPayout(props.payout), [
        props.payout
    ]);
    return /*#__PURE__*/ _jsx(TableList.Row, {
        as: "button",
        testId: testId,
        "data-heapid": "payment",
        rowId: payout.id(),
        items: compact([
            {
                value: /*#__PURE__*/ _jsxs("div", {
                    className: styles.payoutIdCell,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            weight: "normal",
                            className: styles.payoutId,
                            children: payout.formattedNumber()
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.payoutStatus,
                            children: [
                                showQboIntegration && /*#__PURE__*/ _jsx(PayoutAccountingIntegrationStatus, {
                                    accounting: Accounting.QBO,
                                    testId: "".concat(testId, "-qbo"),
                                    integration: payout.qboIntegration()
                                }),
                                showXeroIntegration && /*#__PURE__*/ _jsx(PayoutAccountingIntegrationStatus, {
                                    accounting: Accounting.Xero,
                                    testId: "".concat(testId, "-xero"),
                                    integration: payout.xeroIntegration()
                                })
                            ]
                        })
                    ]
                })
            },
            {
                value: payout.formattedCreatedAt()
            },
            {
                value: null
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    weight: "normal",
                    capitalize: true,
                    testId: "".concat(testId, "-status"),
                    skin: getStatusSkin(payout),
                    children: getStatusText(payout)
                })
            },
            {
                value: payout.amount().display()
            }
        ]),
        expandedContent: ()=>/*#__PURE__*/ _jsx(ExpandedPayoutContent, {
                payout: payout,
                testId: testId,
                invoiceHeaders: invoiceHeaders,
                showQboIntegration: showQboIntegration,
                showXeroIntegration: showXeroIntegration,
                isMobileDevice: isMobileDevice
            }),
        mobile: {
            expandedContent: {
                title: payout.formattedNumber(),
                subtitle: "Deposit date: ".concat(payout.formattedCreatedAt())
            }
        }
    });
}
